import {
  AI_SUMMARY_RECOMMENDATION_ORDER,
  AI_SUMMARY_SERVICE_ORDER,
  AiSummaryInterpretation,
  AiSummaryMeasurement,
  AiSummaryMetric,
  AiSummaryNutrition,
  AiSummaryNutritionDay,
  AiSummaryRecommendationDetail,
  AiSummaryRecommendationItem,
  AiSummaryRecommendations,
  AiSummaryRecommendationValue,
  AiSummaryResponse,
  AiSummarySystem,
  AiSummaryTakeawayItem,
  AiSummaryTrainingDay,
} from "./aiSummaryTypes";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const getString = (value: unknown): string => {
  return typeof value === "string" ? value.trim() : "";
};

const getFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim()) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
  }

  return undefined;
};

const formatLabel = (value: string): string =>
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const parseJsonString = (value: string): unknown => {
  const normalized = value
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "");

  try {
    return JSON.parse(normalized);
  } catch (error) {
    return value;
  }
};

const unwrapResponse = (payload: unknown): unknown => {
  let current =
    typeof payload === "string" ? parseJsonString(payload) : payload;

  for (let depth = 0; depth < 3; depth += 1) {
    if (!isRecord(current) || "key_takeaway" in current) {
      return current;
    }

    const nested = current.data ?? current.response ?? current.result;
    if (nested === undefined || nested === current) {
      return current;
    }

    current = typeof nested === "string" ? parseJsonString(nested) : nested;
  }

  return current;
};

export const parseTakeawayItems = (value: unknown): AiSummaryTakeawayItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<AiSummaryTakeawayItem[]>((items, item) => {
    if (!isRecord(item)) {
      return items;
    }

    const title = getString(item.title);
    const text = getString(item.text);
    const points = Array.isArray(item.points)
      ? item.points.map(getString).filter(Boolean)
      : [];
    if (title || text || points.length > 0) {
      items.push({
        title,
        text,
        ...(points.length > 0 ? { points } : {}),
      });
    }

    return items;
  }, []);
};

const parseMeasurements = (value: unknown): AiSummaryMeasurement[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<AiSummaryMeasurement[]>((measurements, item) => {
    if (!isRecord(item)) {
      return measurements;
    }

    const measurementValue = getFiniteNumber(item.value);
    const dateCreated = getString(item.dateCreated);
    if (measurementValue !== undefined && dateCreated) {
      measurements.push({ value: measurementValue, dateCreated });
    }

    return measurements;
  }, []);
};

const INTERPRETATION_LABELS: Record<number, AiSummaryInterpretation> = {
  1: "Decreasing",
  2: "Increasing",
  3: "Stable",
};

const parseInterpretation = (
  value: unknown
): AiSummaryInterpretation | undefined => {
  const numericValue = getFiniteNumber(value);
  if (numericValue !== undefined) {
    return INTERPRETATION_LABELS[numericValue];
  }

  const stringValue = getString(value).toLowerCase();
  return Object.values(INTERPRETATION_LABELS).find(
    (label) => label.toLowerCase() === stringValue
  );
};

const appendScoreCategory = (title: string, scoreCategory: string): string => {
  if (
    !scoreCategory ||
    title.toLowerCase().endsWith(scoreCategory.toLowerCase())
  ) {
    return title;
  }

  return `${title} ${scoreCategory}`.trim();
};

const parseMetric = (value: unknown): AiSummaryMetric | null => {
  if (!isRecord(value)) {
    return null;
  }

  const name = getString(value.name);
  const rawTitle = getString(value.title);
  const bullets = Array.isArray(value.bullets)
    ? value.bullets.map(getString).filter(Boolean)
    : [];
  const rawScorePercent = getFiniteNumber(value.score_percent);
  const scorePercent =
    rawScorePercent !== undefined
      ? Math.max(0, Math.min(100, rawScorePercent))
      : undefined;
  const numericMetricValue = getFiniteNumber(value.value);
  const stringMetricValue = getString(value.value);
  const metricValue =
    numericMetricValue !== undefined
      ? numericMetricValue
      : stringMetricValue || undefined;
  const unit = typeof value.unit === "string" ? value.unit.trim() : undefined;
  const scoreCategory = getString(value.scoreCategory ?? value.score_category);
  const measurements = parseMeasurements(value.measurements);
  const graphComponent = value.graphComponent ?? value.graph_component;
  const requestsMeasurementHistory =
    graphComponent === true || graphComponent === 1 || graphComponent === "1";
  const interpretation = parseInterpretation(value.interpretation);
  const valueLabel =
    metricValue !== undefined
      ? `${metricValue}${unit ? `${unit === "%" ? "" : " "}${unit}` : ""}`
      : scorePercent !== undefined
      ? `${scorePercent}%`
      : "";
  const titleWithoutCategory =
    scoreCategory && name && valueLabel
      ? `${name} — ${valueLabel}`
      : rawTitle || name || valueLabel;
  const title = appendScoreCategory(titleWithoutCategory, scoreCategory);

  if (!title && bullets.length === 0 && measurements.length === 0) {
    return null;
  }

  return {
    name: name || rawTitle || "Health metric",
    title: title || "Health metric",
    bullets,
    ...(scorePercent !== undefined ? { scorePercent } : {}),
    ...(scoreCategory ? { scoreCategory } : {}),
    ...(metricValue !== undefined ? { value: metricValue } : {}),
    ...(unit !== undefined ? { unit } : {}),
    ...(measurements.length > 0 ? { measurements } : {}),
    ...(requestsMeasurementHistory && measurements.length > 1
      ? { showMeasurementTable: true }
      : {}),
    ...(interpretation ? { interpretation } : {}),
  };
};

const parseMetricArray = (value: unknown): AiSummaryMetric[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(parseMetric)
    .filter((item): item is AiSummaryMetric => item !== null);
};

const parseSystem = (value: unknown): AiSummarySystem | null => {
  if (!isRecord(value)) {
    return null;
  }

  const name = getString(value.name) || getString(value.title);
  const rawScore = getFiniteNumber(value.score);
  const score =
    rawScore !== undefined ? Math.max(0, Math.min(100, rawScore)) : undefined;
  const highlights = parseMetricArray(value.highlights);
  const lowlights = parseMetricArray(value.lowlights);
  const scoreCategory = getString(value.scoreCategory ?? value.score_category);

  if (
    !name &&
    score === undefined &&
    !scoreCategory &&
    highlights.length === 0 &&
    lowlights.length === 0
  ) {
    return null;
  }

  return {
    name: name || "Health system",
    ...(score !== undefined ? { score } : {}),
    ...(scoreCategory ? { scoreCategory } : {}),
    highlights,
    lowlights,
  };
};

const parseSystems = (
  value: unknown,
  legacyTone: "highlight" | "lowlight"
): AiSummarySystem[] => {
  if (!isRecord(value)) {
    return [];
  }

  if (Array.isArray(value.systems)) {
    return value.systems
      .map(parseSystem)
      .filter((system): system is AiSummarySystem => system !== null);
  }

  return Object.entries(value).reduce<AiSummarySystem[]>(
    (systems, [groupName, rawItems]) => {
      const metrics = parseMetricArray(rawItems);
      if (metrics.length > 0) {
        systems.push({
          name: groupName,
          highlights: legacyTone === "highlight" ? metrics : [],
          lowlights: legacyTone === "lowlight" ? metrics : [],
        });
      }
      return systems;
    },
    []
  );
};

const getDetailValue = (value: unknown, suffix = ""): string => {
  const numericValue = getFiniteNumber(value);
  if (numericValue !== undefined) {
    return `${numericValue}${suffix}`;
  }

  const stringValue = getString(value);
  return stringValue ? `${stringValue}${suffix}` : "";
};

const compactDetails = (
  details: Array<AiSummaryRecommendationDetail | null>
): AiSummaryRecommendationDetail[] =>
  details.filter(
    (detail): detail is AiSummaryRecommendationDetail =>
      detail !== null && Boolean(detail.value)
  );

const parseRecommendationObjectItem = (
  value: UnknownRecord
): AiSummaryRecommendationItem | null => {
  const title =
    getString(value.name) || getString(value.title) || getString(value.weekday);
  const description =
    getString(value.activity) ||
    getString(value.description) ||
    getString(value.text);
  const dosage = getString(value.dosage);
  const frequency = getString(value.sessions_frequency);
  const details = compactDetails([
    dosage ? { label: "Dosage", value: dosage } : null,
    frequency ? { label: "Frequency", value: frequency } : null,
  ]);

  if (!title && !description && details.length === 0) {
    return null;
  }

  return {
    title: value.weekday ? formatLabel(title) : title,
    description,
    details,
  };
};

const parseExerciseRecommendation = (
  value: UnknownRecord
): AiSummaryRecommendationItem[] => {
  if (!Array.isArray(value.weekly_plan)) {
    return [];
  }

  return value.weekly_plan.reduce<AiSummaryRecommendationItem[]>(
    (items, day) => {
      if (!isRecord(day)) {
        return items;
      }

      const weekday = getString(day.weekday);
      const activity = getString(day.activity);
      if (weekday || activity) {
        items.push({
          title: weekday ? formatLabel(weekday) : "Training day",
          description: activity,
          details: [],
        });
      }
      return items;
    },
    []
  );
};

const parseNutritionRecommendation = (
  value: UnknownRecord
): AiSummaryRecommendationItem[] => {
  const dayTypes: Array<[string, string]> = [
    ["workout_days", "Workout days"],
    ["non_workout_days", "Non-workout days"],
  ];

  return dayTypes.reduce<AiSummaryRecommendationItem[]>(
    (items, [field, title]) => {
      const rawPlan = value[field];
      if (!isRecord(rawPlan)) {
        return items;
      }

      const calories = getDetailValue(rawPlan.caloric_intake_kcal, " kcal");
      const protein = getDetailValue(rawPlan.protein_gr, " g");
      const carbohydrates = getDetailValue(rawPlan.carbs_gr, " g");
      const fat = getDetailValue(rawPlan.fat_gr, " g");
      const details = compactDetails([
        calories ? { label: "Calories", value: calories } : null,
        protein ? { label: "Protein", value: protein } : null,
        carbohydrates ? { label: "Carbohydrates", value: carbohydrates } : null,
        fat ? { label: "Fat", value: fat } : null,
      ]);

      if (details.length > 0) {
        items.push({ title, description: "", details });
      }
      return items;
    },
    []
  );
};

const parseGenericRecommendationRecord = (
  value: UnknownRecord
): AiSummaryRecommendationItem[] => {
  const directItem = parseRecommendationObjectItem(value);
  if (directItem) {
    return [directItem];
  }

  return Object.entries(value).reduce<AiSummaryRecommendationItem[]>(
    (items, [field, rawValue]) => {
      if (isRecord(rawValue)) {
        const details = Object.entries(rawValue).reduce<
          AiSummaryRecommendationDetail[]
        >((result, [detailField, detailValue]) => {
          const displayValue = getDetailValue(detailValue);
          if (displayValue) {
            result.push({
              label: formatLabel(detailField),
              value: displayValue,
            });
          }
          return result;
        }, []);

        if (details.length > 0) {
          items.push({
            title: formatLabel(field),
            description: "",
            details,
          });
        }
        return items;
      }

      const description = getString(rawValue);
      if (description) {
        items.push({
          title: formatLabel(field),
          description,
          details: [],
        });
      }
      return items;
    },
    []
  );
};

const parseRecommendation = (
  key: keyof AiSummaryRecommendations,
  value: unknown
): AiSummaryRecommendationValue | undefined => {
  if (typeof value === "string") {
    return getString(value) || undefined;
  }

  if (Array.isArray(value)) {
    const stringItems = value.map(getString).filter(Boolean);
    const structuredItems = value.reduce<AiSummaryRecommendationItem[]>(
      (items, item) => {
        if (isRecord(item)) {
          const parsedItem = parseRecommendationObjectItem(item);
          if (parsedItem) {
            items.push(parsedItem);
          }
        }
        return items;
      },
      []
    );

    if (structuredItems.length > 0) {
      return [
        ...stringItems.map((description) => ({
          title: "",
          description,
          details: [],
        })),
        ...structuredItems,
      ];
    }
    return stringItems.length > 0 ? stringItems : undefined;
  }

  if (!isRecord(value)) {
    return undefined;
  }

  const structuredItems =
    key === "exercise"
      ? parseExerciseRecommendation(value)
      : key === "nutrition"
      ? parseNutritionRecommendation(value)
      : parseGenericRecommendationRecord(value);
  return structuredItems.length > 0 ? structuredItems : undefined;
};

const TRAINING_ACTIVITY_LABELS: Record<number, string> = {
  0: "Rest",
  1: "HIIT",
  2: "Strength",
  3: "Cardio",
  4: "Yoga",
  5: "Pilates",
};

const TRAINING_SUBTYPE_LABELS: Record<number, string> = {
  1: "Bodyweight",
  2: "Cardiovascular",
  3: "Stretching",
  4: "Plyometric",
  5: "Strength",
  6: "Balance",
  7: "Functional",
  8: "Isometric",
  9: "Yoga",
  10: "Pilates",
};

const getMappedLabel = (
  value: unknown,
  labels: Record<number, string>
): string => {
  const numericValue = getFiniteNumber(value);
  if (numericValue !== undefined && labels[numericValue]) {
    return labels[numericValue];
  }

  return getString(value);
};

export const parseAiSummaryTrainingPlan = (
  value: unknown
): AiSummaryTrainingDay[] => {
  const source = isRecord(value)
    ? value.weekly_plan ?? value.weeklyPlan
    : value;
  if (!Array.isArray(source)) {
    return [];
  }

  return source.reduce<AiSummaryTrainingDay[]>((days, item) => {
    if (!isRecord(item)) {
      return days;
    }

    const weekday = getString(item.weekday ?? item.day);
    const rawActivity = getString(item.activity ?? item.activity_label);
    const activityParts = rawActivity
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    const primaryParts = (activityParts[0] || "")
      .split("/")
      .map((part) => part.trim())
      .filter(Boolean);
    const explicitActivity = getMappedLabel(
      item.activity_type ?? item.activityType ?? item.type,
      TRAINING_ACTIVITY_LABELS
    );
    const explicitSubtype = getMappedLabel(
      item.sub_type ?? item.subType ?? item.subtype,
      TRAINING_SUBTYPE_LABELS
    );
    const rawPrimaryActivity =
      explicitActivity || primaryParts[0] || activityParts[0] || "";
    const normalizedPrimaryActivity = rawPrimaryActivity.toLowerCase();
    const activity = normalizedPrimaryActivity.includes("strength")
      ? "Strength"
      : normalizedPrimaryActivity.includes("resistance")
      ? "Strength"
      : normalizedPrimaryActivity.includes("cardio") ||
        normalizedPrimaryActivity.includes("endurance")
      ? "Cardio"
      : rawPrimaryActivity;
    const subtype =
      explicitSubtype ||
      activityParts[1] ||
      primaryParts[1] ||
      (["yoga", "pilates"].includes(activity.toLowerCase()) ? activity : "");

    if (weekday || activity || subtype) {
      days.push({
        weekday: weekday ? formatLabel(weekday) : "Training day",
        activity: activity || "Rest",
        ...(subtype ? { subtype: formatLabel(subtype) } : {}),
      });
    }

    return days;
  }, []);
};

const parseAiSummaryNutritionDay = (
  value: unknown
): AiSummaryNutritionDay | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const day: AiSummaryNutritionDay = {
    fatGrams: getFiniteNumber(value.fat_gr ?? value.fatGrams),
    carbsGrams: getFiniteNumber(value.carbs_gr ?? value.carbsGrams),
    proteinGrams: getFiniteNumber(value.protein_gr ?? value.proteinGrams),
    caloricBurnKcal: getFiniteNumber(
      value.caloric_burn_kcal ?? value.caloricBurnKcal
    ),
    caloricIntakeKcal: getFiniteNumber(
      value.caloric_intake_kcal ?? value.caloricIntakeKcal
    ),
  };

  return Object.values(day).some((item) => item !== undefined)
    ? day
    : undefined;
};

export const parseAiSummaryNutrition = (value: unknown): AiSummaryNutrition => {
  const source = isRecord(value) ? value : {};
  return {
    workoutDays: parseAiSummaryNutritionDay(
      source.workout_days ?? source.workoutDays
    ),
    nonWorkoutDays: parseAiSummaryNutritionDay(
      source.non_workout_days ?? source.nonWorkoutDays
    ),
  };
};

export const parseAiSummaryRecommendations = (
  ...sources: unknown[]
): AiSummaryRecommendations => {
  const result: AiSummaryRecommendations = {};

  for (const source of sources) {
    if (!isRecord(source)) {
      continue;
    }
    for (const key of AI_SUMMARY_RECOMMENDATION_ORDER) {
      const value = source[key];
      const recommendation = parseRecommendation(key, value);
      if (recommendation !== undefined) {
        result[key] = recommendation;
      } else if (
        AI_SUMMARY_SERVICE_ORDER.includes(key) &&
        Array.isArray(value) &&
        value.length === 0 &&
        result[key] === undefined
      ) {
        // Preserve an explicitly empty service category for the empty state,
        // without overwriting services supplied by another response section.
        result[key] = [];
      }
    }
  }
  return result;
};

export const parseAiSummaryResponse = (payload: unknown): AiSummaryResponse => {
  const unwrapped = unwrapResponse(payload);
  const root = isRecord(unwrapped) ? unwrapped : {};
  const keyTakeaway = isRecord(root.key_takeaway) ? root.key_takeaway : {};

  const overallPicture = getString(keyTakeaway.overall_picture);
  const highlightsSummary = parseTakeawayItems(keyTakeaway.highlights_summary);
  const lowlightsSummary = parseTakeawayItems(keyTakeaway.lowlights_summary);
  const disclaimer = getString(root.disclaimer);
  const recommendationsRecord = isRecord(root.recommendations)
    ? root.recommendations
    : {};
  const recommendations = parseAiSummaryRecommendations(
    recommendationsRecord,
    root.services
  );

  return {
    keyTakeaway: {
      overallPicture,
      highlightsSummary,
      lowlightsSummary,
    },
    highlights: parseSystems(root.highlights, "highlight"),
    lowlights: parseSystems(root.lowlights, "lowlight"),
    trainingPlan: parseAiSummaryTrainingPlan(recommendationsRecord.exercise),
    nutrition: parseAiSummaryNutrition(recommendationsRecord.nutrition),
    recommendations,
    disclaimer,
  };
};
